import {
  ReactNode,
  forwardRef,
  ReactElement,
  Ref,
  useContext,
} from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Button,
  Dialog,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Card,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  mobileCardSx,
  cardHeaderSx,
  cardSx,
  smallTableHeaderSx,
  smallTableRowSx,
  formatViolationsString,
} from 'utils';
import { useTranslation } from 'react-i18next';
import { Cancel, Warning } from '@mui/icons-material';
import { ViolationsActionHandlers } from './Violations';
import { Rule, Violation } from 'types';
import { ROUTES } from 'router';
import { useFetcher } from 'react-router';
import { AppContext } from 'context/appContext';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

interface ViolationInUseDialogProps {
  handler?: ViolationsActionHandlers | undefined;
  violation: Violation | undefined;
  handleCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  rules: Rule[];
}

export const ViolationInUseDialog = ({
  handler,
  violation,
  handleCancel,
  onConfirm,
  open,
  rules,
}: ViolationInUseDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const theme = useTheme();
  const { palette } = theme;
  const mobile = useMediaQuery(theme.breakpoints.down('lg'));
  const fetcher = useFetcher({ key: 'violations' });

  const handleConfirm = () => {
    fetcher.submit(
      {
        handler: ViolationsActionHandlers.EXCLUDE,
        data: { ...violation },
        tenantID: state.tenant.id,
      },
      {
        method: 'PUT',
        action: ROUTES.VIOLATIONS,
        encType: 'application/json',
      },
    );
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      scroll={'body'}
      fullWidth
      aria-label={`${t('permit.revokeButton')} Dialog`}
      maxWidth={'md'}
      fullScreen={mobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <Card raised sx={mobile ? mobileCardSx : cardSx}>
        <Box
          sx={
            mobile
              ? {
                  display: 'flex',
                  p: { xs: 2, sm: 4 },
                  backgroundColor: palette.secondary.main,
                }
              : cardHeaderSx
          }
        >
          <Box sx={{ flex: 1, p: 0.5 }}>
            <Typography
              variant={'h4'}
              sx={{
                color: palette.secondary.contrastText,
                textAlign: 'left',
              }}
            >
              {t('dialogs.violation_in_use_title', { ns: 'rules' })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ px: 4, pt: 4 }}>
          <Typography align='center' variant='h6'>
            {handler === ViolationsActionHandlers.DELETE
              ? t('dialogs.violation_in_use_summary_delete', {
                  ns: 'rules',
                  name: violation?.name,
                })
              : t('dialogs.violation_in_use_summary_disable', {
                  ns: 'rules',
                  name: violation?.name,
                })}
          </Typography>
        </Box>
        <Box sx={{ p: 4 }}>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow sx={smallTableHeaderSx}>
                  <TableCell align='left' sx={smallTableHeaderSx}>
                    {t('rule.name', { ns: 'entities' })}
                  </TableCell>
                  <TableCell align='left' sx={smallTableHeaderSx}>
                    {t('rule.description', { ns: 'entities' })}
                  </TableCell>
                  <TableCell align='left' sx={smallTableHeaderSx}>
                    {t('rule.status', { ns: 'entities' })}
                  </TableCell>
                  <TableCell align='left' sx={smallTableHeaderSx}>
                    {t('violation.label_plural', { ns: 'entities' })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules &&
                  rules.map((r: Rule, i: number) => (
                    <TableRow key={`${r.name}-${i}`} sx={smallTableRowSx(i, theme)}>
                      <TableCell align='left'>{r.name}</TableCell>
                      <TableCell align='left'>{r.description}</TableCell>
                      <TableCell
                        align='left'
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {r.status}
                      </TableCell>
                      <TableCell align='left'>
                        {formatViolationsString(r.data.violations)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{ p: 4 }}>
          <Stack
            width='100%'
            direction='row'
            spacing={2}
            justifyContent='flex-end'
            mt={2}
            display='flex'
            alignItems='flex-end'
            alignContent='flex-end'
          >
            <Button
              variant={'outlined'}
              onClick={handleCancel}
              color={'secondary'}
              endIcon={<Cancel />}
            >
              {t('cancel')}
            </Button>
            <Button
              variant={'contained'}
              onClick={handleConfirm}
              color={'warning'}
              endIcon={<Warning />}
            >
              {handler === ViolationsActionHandlers.DELETE
                ? t('buttons.delete_violation', { ns: 'rules' })
                : t('buttons.disable_violation', { ns: 'rules' })}
            </Button>
          </Stack>
        </Box>
      </Card>
    </Dialog>
  );
};
